import React, { useState } from 'react';
import { CommunityIdea, CommunityStrategy } from 'types/ApiServicesTypes';
import { useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import ContentBlocksDisplay from './ContentBlocksDisplay';
import { useLikeCommunityIdea, useLikeCommunityStrategy } from 'services/Community';
import { 
  Favorite, 
  FavoriteBorder, 
  ChatBubbleOutline, 
  HourglassEmpty
} from '@mui/icons-material';
import { Chip } from '@mui/material';

// Extended types to include content blocks
type CommunityPostWithContentBlocks = (CommunityIdea | CommunityStrategy) & {
  content_blocks?: {
    type: string;
    content: string;
    image_url?: string;
    image_alt?: string;
    sequence?: number;
  }[];
};

interface PostCardProps {
  post: CommunityPostWithContentBlocks;
  onClick: () => void;  
}

const PostCard: React.FC<PostCardProps> = ({ post, onClick }) => {
  const theme = useTheme();
  const history = useHistory();
  const [isNavigatingToComments, setIsNavigatingToComments] = useState(false);
  
  // Like hooks
  const likeIdeaMutation = useLikeCommunityIdea();
  const likeStrategyMutation = useLikeCommunityStrategy();
  
  // Helper function to get tag name from tags array
  const getTagName = () => {
    if (post.tags && post?.tags?.length > 0) {
      return post.tags[0].name;
    }
    return 'GENERAL';
  };

  // Helper function to get category name
  const getCategoryName = () => {
    return post.category?.name || 'Uncategorized';
  };

  // Handle comment click - navigate to post detail and scroll to comments
  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the main card click
    
    if (isNavigatingToComments) return; // Prevent multiple clicks
    
    setIsNavigatingToComments(true);
    
    // Determine post type and navigate
    const postType = (post as CommunityIdea).likes_count !== undefined ? 'ideas' : 'strategies';
    const postId = post.id;
    
    // Navigate to post detail page with comment section hash
    history.push(`/community/${postType}/${postId}#comments`);
    
    // Reset navigation state after a short delay
    setTimeout(() => setIsNavigatingToComments(false), 1000);
  };

  // Handle like click
  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the main card click
    
    // Determine if this is an idea or strategy and call appropriate like function
    const isIdea = (post as CommunityIdea).likes_count !== undefined;
    
    if (isIdea) {
      likeIdeaMutation.mutate(post.id);
    } else {
      likeStrategyMutation.mutate(post.id);
    }
  };

  return (
    <div
      className="community-post-card"
      onClick={onClick}
      style={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius * 1.5,
        padding: 0,
        marginBottom: 24,
        background: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 420,
        transition: theme.transitions.create(['box-shadow', 'transform'], {
          duration: theme.transitions.duration.short,
        }),
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = theme.shadows[4];
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = theme.shadows[1];
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ padding: '20px 20px 0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 20, flex: 1 }}>{post.title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {post.category?.name && (<Chip
            label={post.category?.name}
            size="small"
            color="infolight"
          />)}
        </div>
      </div>
      {post?.images?.length > 0 && (
        <img
          src={post.images[0]}
          alt="post"
          style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 8, margin: '0 0 0 0' }}
        />
      )}
      <div style={{ padding: '0px 20px 0 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Display content blocks if available */}
        {post.content_blocks && post.content_blocks.length > 0 && (
          <ContentBlocksDisplay 
            contentBlocks={post.content_blocks} 
            showFullContent={false}
          />
        )}
      </div>
      <div style={{ padding: '0 20px 0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <img
          src={"data:image/png;base64, " + post.author?.avatar}
          alt={post.author.name}
          style={{ 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            marginRight: 8, 
            border: `1px solid ${theme.palette.divider}` 
          }}
        />
          <span style={{ fontWeight: 600, fontSize: 14 }}>{post.author.name}</span>
          {post.author.badge && (
            <span style={{ 
              background: theme.palette.success.light, 
              color: theme.palette.success.main, 
              fontWeight: 700, 
              fontSize: 12, 
              borderRadius: theme.shape.borderRadius, 
              padding: '2px 8px', 
              marginLeft: 8 
            }}>
              {post.author.badge}
            </span>
          )}
          <span style={{ 
            marginLeft: 'auto', 
            color: theme.palette.text.secondary, 
            fontSize: 13 
          }}>
            {new Date(post.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px 16px 20px', marginTop: 'auto', gap: 20 }}>
        <span 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            color: theme.palette.text.secondary, 
            fontSize: 15,
            cursor: isNavigatingToComments ? 'not-allowed' : 'pointer',
            padding: '4px 2px',
            borderRadius: theme.shape.borderRadius,
            opacity: isNavigatingToComments ? 0.6 : 1,
            transition: theme.transitions.create(['background-color', 'color', 'opacity', 'transform'], {
              duration: theme.transitions.duration.short,
            }),
          }}
          onMouseEnter={(e) => {
            if (isNavigatingToComments) return;
            e.currentTarget.style.backgroundColor = theme.palette.action.hover;
            e.currentTarget.style.color = theme.palette.primary.main;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = theme.palette.text.secondary;
          }}
          onClick={(e) => {
            handleCommentClick(e);
            // Add a subtle scale animation on click
            e.currentTarget.style.transform = 'scale(1.1)';
            setTimeout(() => {
              e.currentTarget && (e.currentTarget.style.transform = 'scale(1)');
            }, 150);
          }}
        >
          {isNavigatingToComments ? (
            <HourglassEmpty sx={{ fontSize: 16 }} aria-label="Loading" />
          ) : (
            <ChatBubbleOutline sx={{ fontSize: 16 }} aria-label="Comments" />
          )}
          {post.comments_count}
        </span>
        <span 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            color: post.is_liked ? theme.palette.error.main : theme.palette.text.secondary, 
            fontSize: 15,
            cursor: likeIdeaMutation.isLoading || likeStrategyMutation.isLoading ? 'not-allowed' : 'pointer',
            padding: '4px 2px',
            borderRadius: theme.shape.borderRadius,
            opacity: likeIdeaMutation.isLoading || likeStrategyMutation.isLoading ? 0.6 : 1,
            transition: theme.transitions.create(['background-color', 'color', 'opacity', 'transform'], {
              duration: theme.transitions.duration.short,
            }),
          }}
          onMouseEnter={(e) => {
            if (likeIdeaMutation.isLoading || likeStrategyMutation.isLoading) return;
            e.currentTarget.style.backgroundColor = theme.palette.action.hover;
            if (!post.is_liked) {
              e.currentTarget.style.color = theme.palette.error.main;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = post.is_liked ? theme.palette.error.main : theme.palette.text.secondary;
          }}
          onClick={(e) => {
            handleLikeClick(e);
            // Add a subtle scale animation on click
            e.currentTarget.style.transform = 'scale(1.1)';
            setTimeout(() => {
              e.currentTarget && (e.currentTarget.style.transform = 'scale(1)');
            }, 150);
          }}
        >
          {likeIdeaMutation.isLoading || likeStrategyMutation.isLoading ? (
            <HourglassEmpty sx={{ fontSize: 16 }} aria-label="Loading" />
          ) : post.is_liked ? (
            <Favorite sx={{ fontSize: 16 }} aria-label="Liked" />
          ) : (
            <FavoriteBorder sx={{ fontSize: 16 }} aria-label="Like" />
          )}
          {post.likes_count}
        </span>
      </div>
    </div>
  );
};

export default PostCard; 